/* eslint-disable no-await-in-loop */
import JSPDF from 'jspdf';
import { Option, getOrElse } from 'fp-ts/Option';
import { ReportInstance } from 'domain/entity/reportInstance';
import { A4_PADDING_X, A4_PADDING_Y, A4_ROWS_PER_PAGE, CELL_MARGIN, COLUMNS } from 'adapter/in/dashboards/components/Grid';
import Layout, { LayoutElementType, LayoutType } from 'domain/entity/layout';
import UnicodeRegular from 'asset/font/DejaVuSans-Regular';
import UnicodeBold from 'asset/font/DejaVuSans-Bold';
import {
    ensureTextBlockFontInjected,
    loadTextBlockFontOnce,
    normalizeTextBlockLists,
    rgbaToRgbString,
    undoNormalizeTextBlockLists,
} from 'adapter/in/reports/utils/generatePDFUtils';
import { replaceSvgsWithImages } from 'adapter/in/reports/utils/reportRendering';
import { withDashboardFrame } from 'adapter/in/dashboards/containers/Grid/utils';
import { mapLayoutWidgetsToGridElements } from 'adapter/in/dashboards/components/Grid/gridUtils';

export type Undo = () => void;

export const generateVectorizedPDF = async (
    reportRoot: HTMLDivElement,
    reportInstance: ReportInstance,
    layoutData: Option<Layout[]>,
    PDFName?: string
): Promise<string> => {
    try {
        const layouts: Layout[] = getOrElse<Layout[]>(() => [])(layoutData);
        const a4Layout = layouts.find((l) => l.type === LayoutType.A4);
        if (!a4Layout) throw new Error('No A4 layout found');

        const gridElements = mapLayoutWidgetsToGridElements(a4Layout, false);

        const printableWidgets = withDashboardFrame(gridElements);

        const pagedWidgets = printableWidgets.map((widget) => {
            const page = Math.floor(widget.y / A4_ROWS_PER_PAGE);
            const rowStart = widget.y % A4_ROWS_PER_PAGE;

            return {
                widget,
                page,
                rowStart,
            };
        });

        const userWidgets = printableWidgets.filter(
            (w) => w.type !== LayoutElementType.A4_HEADER && w.type !== LayoutElementType.A4_FOOTER
        );

        const maxRow = Math.max(0, ...userWidgets.map((w) => w.y + w.h));
        const totalPages = Math.ceil(maxRow / A4_ROWS_PER_PAGE);

        const pdf = new JSPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
            compress: true,
            putOnlyUsedFonts: true,
            floatPrecision: 'smart',
        });

        pdf.setProperties({
            title: PDFName || reportInstance.name,
            subject: 'Report',
            author: 'Spacewell Energy',
            creator: 'Spacewell Energy - Report Generator',
        });

        pdf.addFileToVFS('UnicodeRegular.ttf', UnicodeRegular);
        pdf.addFileToVFS('UnicodeBold.ttf', UnicodeBold);
        pdf.addFont('UnicodeRegular.ttf', 'dejavusans', 'normal');
        pdf.addFont('UnicodeBold.ttf', 'dejavusans', 'bold');
        pdf.setFont('dejavusans', 'normal');

        ensureTextBlockFontInjected();
        await loadTextBlockFontOnce();
        await document.fonts.ready;

        for (let i = 1; i < totalPages; i += 1) {
            pdf.addPage();
        }

        const pageHeight = pdf.internal.pageSize.getHeight();
        const pageWidth = pdf.internal.pageSize.getWidth();

        const usablePageWidth = pageWidth - A4_PADDING_X * 2;
        const usablePageHeight = pageHeight - A4_PADDING_Y * 2;

        const rowsPerPage = A4_ROWS_PER_PAGE;
        const columns = COLUMNS[LayoutType.A4];

        const rowHeightPt = (usablePageHeight - (rowsPerPage - 1) * CELL_MARGIN) / rowsPerPage;
        const colWidthPt = (usablePageWidth - (columns - 1) * CELL_MARGIN) / columns;

        // eslint-disable-next-line no-restricted-syntax
        for (const { widget, page } of pagedWidgets) {
            const { i: id, x, y, w: width, h: height } = widget;

            const widgetElement = reportRoot.querySelector(`[data-widget-id="${id}"]`) as HTMLElement | null;

            if (!widgetElement) {
                console.warn('Widget not found in DOM:', id);
                // eslint-disable-next-line no-continue
                continue;
            }

            pdf.setPage(page + 1);

            const xPt = A4_PADDING_X + x * (colWidthPt + CELL_MARGIN);

            const yOnPage = A4_PADDING_Y + (y % rowsPerPage) * (rowHeightPt + CELL_MARGIN);
            const wPt = width * colWidthPt + (width - 1) * CELL_MARGIN;
            const hPt = height * rowHeightPt + (height - 1) * CELL_MARGIN;

            const svg = widgetElement.querySelector('.highcharts-container svg') as SVGSVGElement | null;

            if (svg) {
                const clonedSvg = svg.cloneNode(true) as SVGElement;

                clonedSvg.style.setProperty('font-family', 'dejavusans');
                clonedSvg.style.setProperty('font-size', '0.8em');

                const isPieChart = clonedSvg.querySelector('.highcharts-pie-series') !== null;
                if (isPieChart) {
                    clonedSvg.querySelectorAll('.highcharts-data-label text').forEach((label) => {
                        label.firstChild?.remove();
                    });
                }

                await pdf.svg(clonedSvg, {
                    x: xPt,
                    y: yOnPage,
                    width: wPt,
                    height: hPt,
                });
            } else {
                const isTipTap = !!widgetElement.querySelector('.ProseMirror');
                if (!isTipTap) {
                    widgetElement.style.setProperty('font-family', 'dejavusans');
                }
                widgetElement.style.setProperty('letter-spacing', '0.01px');

                await replaceSvgsWithImages(widgetElement);

                rgbaToRgbString(widgetElement);

                const pixelRatio = window.devicePixelRatio || 1;
                const scale = Math.max(2, pixelRatio * 2);

                const html2canvas = (await import('html2canvas')).default;

                const listUndos: Undo[] = [];
                normalizeTextBlockLists(widgetElement, listUndos);

                const canvas = await html2canvas(widgetElement, {
                    scale,
                    backgroundColor: '#FFFFFF',
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                });

                undoNormalizeTextBlockLists(listUndos);

                const jpg = canvas.toDataURL('image/jpeg', 0.78);

                pdf.addImage(jpg, 'JPEG', xPt, yOnPage, wPt, hPt);
            }
        }
        const blob = pdf.output('blob');
        return URL.createObjectURL(blob);
    } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
    }
};
